import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTableHandle } from '@/components/ui/DataTable';

interface ExportCsvButtonProps {
  dataTableRef: React.RefObject<DataTableHandle | null>;
  /** Downloaded file name, e.g. 'sites.csv'. */
  filename: string;
  /** The post-filter row count DataTable reports via onExportRowCountChange — disables the button at 0 so it never offers a headers-only export. */
  rowCount: number;
  /**
   * Overrides the default `dataTableRef.current.exportCsv()` behavior — for a
   * server-paginated table, `dataTableRef` only holds the current page, so
   * the caller instead fetches every filtered row itself (e.g. via
   * `exportRows` from `csvExport.ts`) and builds the CSV from that.
   */
  onExport?: () => void | Promise<void>;
}

export const ExportCsvButton: React.FC<ExportCsvButtonProps> = ({
  dataTableRef,
  filename,
  rowCount,
  onExport,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleClick = async () => {
    if (!onExport) {
      dataTableRef.current?.exportCsv(filename);
      return;
    }
    setIsExporting(true);
    try {
      await onExport();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={rowCount === 0 || isExporting}
      className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2"
    >
      <Download className="w-4 h-4" />
      <span>{isExporting ? 'Exporting…' : 'Export CSV'}</span>
    </Button>
  );
};
